import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { ThemeSelector } from "../common/ThemeSelector";

const Navbar = ({ className = "" }) => {
    const navigate = useNavigate();
    const { user, signOut } = useAuth();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleNavigation = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <header
            className={`navbar fixed top-0 left-0 right-0 z-50 px-4 md:px-12 lg:px-20 py-4 transition-all duration-300 ${isScrolled ? 'bg-base-100/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
                } ${className}`}
        >
            {/* Logo */}
            <div className="flex-1">
                <div
                    className="flex items-center cursor-pointer group"
                    onClick={() => handleNavigation('/')}
                >
                    <img
                        className="w-10 h-10 md:w-12 md:h-12 transition-transform group-hover:scale-105"
                        loading="lazy"
                        alt="StockAlgoTrader Logo"
                        src="/group-39795.svg"
                    />
                    <span className="ml-2 md:ml-3 text-xl md:text-2xl font-bold text-gradient-primary font-heading">
                        StockAlgoTrader
                    </span>
                </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex flex-none">
                <nav className="flex items-center gap-3">
                    <ThemeSelector />
                    {user ? (
                        <>
                            <button
                                className="btn btn-ghost hover:bg-primary/10"
                                onClick={() => handleNavigation('/dashboard')}
                            >
                                Dashboard
                            </button>
                            <div className="dropdown dropdown-end">
                                <div tabIndex={0} role="button" className="btn btn-ghost gap-2">
                                    <div className="avatar placeholder">
                                        <div className="bg-primary text-primary-content rounded-full w-9">
                                            <span className="text-sm font-semibold">
                                                {user.name ? user.name[0].toUpperCase() : 'U'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <ul tabIndex={0} className="dropdown-content menu bg-base-200 rounded-xl z-[1] w-52 p-2 shadow-xl mt-2">
                                    <li><button onClick={() => handleNavigation('/dashboard')}>Dashboard</button></li>
                                    <li><button onClick={() => { signOut(); handleNavigation('/'); }}>Logout</button></li>
                                </ul>
                            </div>
                        </>
                    ) : (
                        <>
                            <button
                                className="btn btn-ghost hover:bg-primary/10"
                                onClick={() => handleNavigation('/signin')}
                            >
                                Sign In
                            </button>
                            <button
                                className="btn btn-primary shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-shadow"
                                onClick={() => handleNavigation('/signup')}
                            >
                                Start Free Trial
                            </button>
                        </>
                    )}
                </nav>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex-none">
                <button
                    className="btn btn-ghost btn-square"
                    onClick={() => setMobileMenuOpen(true)}
                    aria-label="Open menu"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-6 h-6 stroke-current">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                </button>
            </div>

            {/* Mobile Drawer */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-[100]">
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => setMobileMenuOpen(false)}
                    />
                    <div className="absolute right-0 top-0 h-full w-72 bg-base-100 shadow-2xl p-6 animate-slide-in-right">
                        <div className="flex justify-end mb-6">
                            <button
                                className="btn btn-ghost btn-square"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-6 h-6 stroke-current">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="mb-6">
                            <ThemeSelector />
                        </div>

                        <nav className="flex flex-col gap-2">
                            <button
                                className="btn btn-ghost justify-start"
                                onClick={() => handleNavigation('/')}
                            >
                                Home
                            </button>
                            {user ? (
                                <>
                                    <button
                                        className="btn btn-ghost justify-start"
                                        onClick={() => handleNavigation('/dashboard')}
                                    >
                                        Dashboard
                                    </button>
                                    <button
                                        className="btn btn-ghost justify-start text-error"
                                        onClick={() => { signOut(); handleNavigation('/'); }}
                                    >
                                        Logout
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        className="btn btn-ghost justify-start"
                                        onClick={() => handleNavigation('/signin')}
                                    >
                                        Sign In
                                    </button>
                                    <button
                                        className="btn btn-primary w-full mt-4"
                                        onClick={() => handleNavigation('/signup')}
                                    >
                                        Start Free Trial
                                    </button>
                                </>
                            )}
                        </nav>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
